import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import moment from "moment";
import { getFullName, getKycChip, stringAvatar } from "../../../utils/helpers";
import { useState } from "react";
import ActionDialog from "../../../Components/ActionDialog";
import UserActions from "./UserActions";
import { GppBad, VerifiedUser } from "@mui/icons-material";

const DetailItem = ({ label, value }) => (
  <Grid item xs={12} sm={6} md={3} lg={3}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    {value || "N/A"}
  </Grid>
);

const UserInfoCard = ({ user, fetchData, showActions = true }) => {
  const [dialog, setDialog] = useState({
    open: false,
    type: null,
    label: null,
  });

  if (!user) {
    return null; // Or a loading indicator
  }

  const closeDialog = () => setDialog({ open: false, type: null, label: null });

  const fullName = getFullName(user);

  const kycStatusConfig = {
    active: { label: "Verified", color: "success", icon: <VerifiedUser /> },
    suspended: { label: "Suspended", color: "warning", icon: <GppBad /> },
    inactive: { label: "Pending", color: "error" },
  };

  const kycChip = kycStatusConfig[user.kyc_status] || kycStatusConfig.inactive;

  return (
    <Card>
      <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
        <ActionDialog
          open={dialog.open}
          onClose={closeDialog}
          dialogType={dialog.type}
          dialogLabel={dialog.label}
          user={user}
          onSuccess={fetchData}
        />
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          sx={{ mb: 2 }}
        >
          <Stack direction="row" alignItems="top" spacing={2}>
            <Avatar
              {...stringAvatar(`${user.first_name} ${user.other_name}`)}
              sx={{
                width: 64,
                height: 64,
                fontSize: 24,
                bgcolor: "primary.main",
                color: "#fff",
              }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {user.role}
              </Typography>
              <Typography variant="body2">Platform ID: {user.id}</Typography>
              <Typography
                variant="h6"
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  flexWrap: "wrap",
                  alignItems: { xs: "flex-start", sm: "center" },
                  gap: { xs: 1, sm: 2 },
                }}
              >
                {fullName}
                <Tooltip title={`KYC Status: ${kycChip.label}`}>
                  <Chip
                    icon={kycChip.icon}
                    label={kycChip.label}
                    color={kycChip.color}
                    size="small"
                  />
                </Tooltip>
              </Typography>
            </Box>
          </Stack>

          {showActions && (
            <Stack
              spacing={2}
              alignItems={{ xs: "flex-start", sm: "center" }}
              sx={{ mb: 2, width: { md: "50%" } }}
            >
              <UserActions _user={user} fetchData={fetchData} />
            </Stack>
          )}
        </Stack>

        <Divider sx={{ my: 1 }} />

        <Grid container spacing={2}>
          <DetailItem
            label="Phone Number"
            value={
              <Typography variant="body1" fontWeight="medium">
                {user.msisdn}
              </Typography>
            }
          />
          <DetailItem
            label="ID Number"
            value={
              <Typography variant="body1" fontWeight="medium">
                {user.id_number}
              </Typography>
            }
          />
          <DetailItem
            label="Date Joined"
            value={
              <Typography variant="body1" fontWeight="medium">
                {moment(user.start_date).format("DD MMM YYYY")}
              </Typography>
            }
          />
          <DetailItem
            label="Active Status"
            value={
              <Tooltip title={`Active Status: ${user.active_status}`}>
                {getKycChip(user.active_status)}
              </Tooltip>
            }
          />
        </Grid>
      </CardContent>
    </Card>
  );
};

export default UserInfoCard;
